// src/components/CardEditor/SupportEditor.tsx
// ✅ ATUALIZADO: Upload via Cloudinary (igual ao RichTextEditor)

import React, { useRef, useEffect, useState } from 'react';
import {
    Bold,
    Italic,
    Underline,
    Superscript,
    Palette,
    Highlighter,
    List,
    ListOrdered,
    Image as ImageIcon,
    X,
    RotateCcw,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Music
} from 'lucide-react';
import './SupportEditor.css';
import { ImageZoomModal } from './ImageZoomModal';

interface SupportEditorProps {
    content: string;
    setContent: (content: string) => void;
    placeholder?: string;
    subdeckName?: string; // ✅ NOVO
}

export const SupportEditor: React.FC<SupportEditorProps> = ({
    content,
    setContent,
    placeholder = 'Insira o conteúdo de apoio',
    subdeckName = 'geral' // ✅ NOVO
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showHighlightPicker, setShowHighlightPicker] = useState(false);
    const [zoomImage, setZoomImage] = useState<string | null>(null);
    const [zoomScale, setZoomScale] = useState<number>(1);
    const [showAudioMenu, setShowAudioMenu] = useState(false);

    const textColors = [
        '#000000', '#dc2626', '#2563eb', '#059669', '#7c3aed', '#ea580c',
        '#be123c', '#0284c7', '#16a34a', '#db2777', '#92400e', '#4b5563'
    ];

    const highlightColors = [
        '#fef08a', '#bbf7d0', '#a5f3fc', '#f9a8d4', '#fed7aa', '#fecdd3',
        '#d9f99d', '#bfdbfe', '#fde68a', '#fbcfe8', '#d1fae5', '#e9d5ff'
    ];

    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== content) {
            editorRef.current.innerHTML = content;
        }
    }, [content]);

    useEffect(() => {
        if (!editorRef.current) return;

        const images = editorRef.current.querySelectorAll('.support-inline-image');
        images.forEach((img) => {
            const htmlImg = img as HTMLImageElement;
            const newImg = htmlImg.cloneNode(true) as HTMLImageElement;
            htmlImg.parentNode?.replaceChild(newImg, htmlImg);
            newImg.addEventListener('click', () => setZoomImage(newImg.src));
        });
    }, [content]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.audio-dropdown-container')) {
                setShowAudioMenu(false);
            }
        };

        if (showAudioMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showAudioMenu]);

    const handleInput = () => {
        if (editorRef.current) {
            setContent(editorRef.current.innerHTML);
        }
    };

    const execCommand = (command: string, value: string | null = null) => {
        document.execCommand(command, false, value ?? undefined);
        editorRef.current?.focus();
    };

    const applyTextColor = (color: string) => {
        document.execCommand('foreColor', false, color);
        setShowColorPicker(false);
        editorRef.current?.focus();
    };

    const applyHighlight = (color: string) => {
        document.execCommand('backColor', false, color);
        setShowHighlightPicker(false);
        editorRef.current?.focus();
    };

    const insertLetterList = () => {
        if (!editorRef.current) return;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const ol = document.createElement('ol');
        ol.className = 'letter-list';

        const li = document.createElement('li');
        li.innerHTML = '&nbsp;';
        ol.appendChild(li);

        range.deleteContents();
        range.insertNode(ol);

        range.selectNodeContents(li);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);

        handleInput();
    };

    const insertHTMLAtCursor = (html: string) => {
        if (!editorRef.current) return;

        editorRef.current.focus();
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;

        const range = sel.getRangeAt(0);
        range.deleteContents();

        const tempEl = document.createElement('div');
        tempEl.innerHTML = html;

        const frag = document.createDocumentFragment();
        let node: ChildNode | null;
        let lastNode: ChildNode | null = null;
        while ((node = tempEl.firstChild)) {
            lastNode = frag.appendChild(node);
        }

        range.insertNode(frag);

        if (lastNode) {
            range.setStartAfter(lastNode);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
        }

        handleInput();
    };

    // ✅ UPLOAD DE IMAGEM - CLOUDINARY
    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !editorRef.current) return;

        const formData = new FormData();
        formData.append('image', file);
        formData.append('subdeckName', subdeckName); // ✅ NOVO

        try {
            const token = localStorage.getItem('accessToken');

            // ✅ NOVA URL: Cloudinary
            const response = await fetch('/api/upload/card-image-cloudinary', {
                method: 'POST',
                body: formData,
                headers: { Authorization: `Bearer ${token}` }
            });

            const data = await response.json();
            if (!response.ok || !data.url) {
                alert(data.message || 'Erro ao enviar imagem');
                return;
            }

            console.log('✅ Imagem salva no Cloudinary (support):', data.url);

            const imageHTML = `<img src="${data.url}" class="support-inline-image";" />`;
            insertHTMLAtCursor(imageHTML);
            editorRef.current.focus();

        } catch (err) {
            console.error('Erro no upload:', err);
        } finally {
            event.target.value = '';
        }
    };

    // ✅ UPLOAD DE ÁUDIO - CLOUDINARY
    const handleAudioUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !editorRef.current) return;

        const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav'];
        if (!allowedTypes.includes(file.type)) {
            alert('Formato não suportado. Use MP3 ou WAV.');
            return;
        }

        if (file.size > 20 * 1024 * 1024) {
            alert('Arquivo muito grande. Máximo: 20MB');
            return;
        }

        const formData = new FormData();
        formData.append('audio', file);
        formData.append('subdeckName', subdeckName); // ✅ NOVO

        try {
            const token = localStorage.getItem('accessToken');

            // ✅ NOVA URL: Cloudinary
            const response = await fetch('/api/upload/card-audio-cloudinary', {
                method: 'POST',
                body: formData,
                headers: { Authorization: `Bearer ${token}` }
            });

            const data = await response.json();
            if (!response.ok || !data.url) {
                alert(data.message || 'Erro ao enviar áudio');
                return;
            }

            console.log('✅ Áudio salvo no Cloudinary (support):', data.url);

            insertHTMLAtCursor(`<audio controls class="support-inline-audio" src="${data.url}" style="width: 100%; max-width: 500px; margin: 20px auto; display: block; border-radius: 8px;"></audio>`);
            editorRef.current.focus();
            setShowAudioMenu(false);

        } catch (err) {
            console.error('Erro no upload de áudio:', err);
            alert('Erro ao fazer upload do áudio');
        } finally {
            event.target.value = '';
        }
    };

    const hasAudio = () => {
        return editorRef.current?.querySelector('.support-inline-audio') !== null;
    };

    const handleRemoveAudio = () => {
        if (!editorRef.current) return;
        const audioElement = editorRef.current.querySelector('.support-inline-audio');
        if (audioElement) {
            audioElement.remove();
            handleInput();
        }
        setShowAudioMenu(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;

        if (e.key === 'Enter') {
            let node = sel.getRangeAt(0).commonAncestorContainer as HTMLElement;
            while (node && node !== editorRef.current) {
                if (node.tagName === 'LI' && node.parentElement?.classList.contains('letter-list')) {
                    e.preventDefault();
                    const li = document.createElement('li');
                    li.innerHTML = '&nbsp;';
                    node.parentElement.insertBefore(li, node.nextSibling);
                    const range = document.createRange();
                    range.selectNodeContents(li);
                    range.collapse(false);
                    sel.removeAllRanges();
                    sel.addRange(range);
                    handleInput();
                    return;
                }
                node = node.parentElement as HTMLElement;
            }
        }

        if (e.key === 'Tab' || e.key === ' ') {
            const range = sel.getRangeAt(0);
            let targetImg: HTMLImageElement | null = null;

            if (range.startContainer.childNodes[range.startOffset] instanceof HTMLImageElement) {
                targetImg = range.startContainer.childNodes[range.startOffset] as HTMLImageElement;
            } else if (range.startContainer.childNodes[range.startOffset - 1] instanceof HTMLImageElement) {
                targetImg = range.startContainer.childNodes[range.startOffset - 1] as HTMLImageElement;
            }

            if (targetImg && targetImg.classList.contains('support-inline-image')) {
                e.preventDefault();
                const currentMargin = parseInt(targetImg.style.marginLeft || '0');
                const step = e.key === 'Tab' ? 40 : 10;

                if (e.shiftKey) {
                    targetImg.style.marginLeft = `${Math.max(0, currentMargin - step)}px`;
                } else {
                    targetImg.style.marginLeft = `${currentMargin + step}px`;
                }
                handleInput();
            }
        }
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.color-picker-container')) {
                setShowColorPicker(false);
                setShowHighlightPicker(false);
            }
        };

        if (showColorPicker || showHighlightPicker) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showColorPicker, showHighlightPicker]);

    return (
        <div className="support-editor-container">
            <div className="support-editor-toolbar">
                <button type="button" onClick={() => execCommand('bold')} className="toolbar-btn" title="Negrito">
                    <Bold size={16} />
                </button>
                <button type="button" onClick={() => execCommand('italic')} className="toolbar-btn" title="Itálico">
                    <Italic size={16} />
                </button>
                <button type="button" onClick={() => execCommand('underline')} className="toolbar-btn" title="Sublinhado">
                    <Underline size={16} />
                </button>
                <button type="button" onClick={() => execCommand('superscript')} className="toolbar-btn" title="Sobrescrito">
                    <Superscript size={16} />
                </button>

                <div className="toolbar-divider" />

                <div className="color-picker-container">
                    <button
                        type="button"
                        className="toolbar-btn"
                        onClick={() => setShowColorPicker(!showColorPicker)}
                        title="Cor do texto"
                    >
                        <Palette size={16} />
                    </button>
                    {showColorPicker && (
                        <div className="color-dropdown">
                            {textColors.map(c => (
                                <div
                                    key={c}
                                    className="color-option"
                                    style={{ backgroundColor: c }}
                                    onClick={() => applyTextColor(c)}
                                    title={c}
                                />
                            ))}
                            <button
                                type="button"
                                className="remove-highlight-btn"
                                onClick={() => {
                                    applyTextColor('#000000');
                                    setShowColorPicker(false);
                                }}
                            >
                                <RotateCcw size={14} />
                                Resetar
                            </button>
                        </div>
                    )}
                </div>

                <div className="color-picker-container">
                    <button
                        type="button"
                        className="toolbar-btn"
                        onClick={() => setShowHighlightPicker(!showHighlightPicker)}
                        title="Grifar texto"
                    >
                        <Highlighter size={16} />
                    </button>
                    {showHighlightPicker && (
                        <div className="color-dropdown">
                            {highlightColors.map(c => (
                                <div
                                    key={c}
                                    className="color-option"
                                    style={{ backgroundColor: c }}
                                    onClick={() => applyHighlight(c)}
                                    title={c}
                                />
                            ))}
                            <button
                                type="button"
                                className="remove-highlight-btn"
                                onClick={() => {
                                    applyHighlight('transparent');
                                    setShowHighlightPicker(false);
                                }}
                            >
                                <X size={14} />
                                Remover grifo
                            </button>
                        </div>
                    )}
                </div>

                <div className="toolbar-divider" />

                <button type="button" onClick={() => execCommand('insertOrderedList')} className="toolbar-btn" title="Lista numerada">
                    <ListOrdered size={16} />
                </button>
                <button type="button" onClick={() => execCommand('insertUnorderedList')} className="toolbar-btn" title="Lista com marcadores">
                    <List size={16} />
                </button>
                <button type="button" onClick={insertLetterList} className="toolbar-btn toolbar-btn-text" title="Lista alfabética">
                    a)
                </button>

                <div className="toolbar-divider" />

                <button type="button" onClick={() => execCommand('justifyLeft')} className="toolbar-btn" title="Alinhar à esquerda">
                    <AlignLeft size={16} />
                </button>
                <button type="button" onClick={() => execCommand('justifyCenter')} className="toolbar-btn" title="Centralizar">
                    <AlignCenter size={16} />
                </button>
                <button type="button" onClick={() => execCommand('justifyRight')} className="toolbar-btn" title="Alinhar à direita">
                    <AlignRight size={16} />
                </button>
                <button type="button" onClick={() => execCommand('justifyFull')} className="toolbar-btn" title="Justificar">
                    <AlignJustify size={16} />
                </button>

                <div className="toolbar-divider" />

                <label className="toolbar-btn" title="Adicionar imagem">
                    <ImageIcon size={16} />
                    <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                </label>

                <div className="audio-dropdown-container">
                    <button
                        type="button"
                        className="toolbar-btn"
                        onClick={() => setShowAudioMenu(!showAudioMenu)}
                        title="Gerenciar áudio"
                    >
                        <Music size={16} />
                    </button>
                    {showAudioMenu && (
                        <div className="audio-dropdown-menu">
                            <label className="audio-dropdown-option">
                                <span>Inserir áudio</span>
                                <input
                                    type="file"
                                    accept="audio/mpeg,audio/mp3,audio/wav"
                                    onChange={handleAudioUpload}
                                    style={{ display: 'none' }}
                                />
                            </label>
                            <button
                                type="button"
                                className="audio-dropdown-option audio-remove-option"
                                onClick={handleRemoveAudio}
                                disabled={!hasAudio()}
                            >
                                <span>Remover áudio</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                onKeyDown={handleKeyDown}

                onClick={(e) => {
                    const editor = editorRef.current;
                    if (!editor) return;

                    if (e.target === editor) {
                        const images = editor.querySelectorAll('.support-inline-image');
                        let foundSpace = false;

                        images.forEach((img) => {
                            const rect = (img as HTMLElement).getBoundingClientRect();
                            if (e.clientY >= rect.top && e.clientY <= rect.bottom && e.clientX < rect.left) {
                                const range = document.createRange();
                                const sel = window.getSelection();
                                range.setStartBefore(img);
                                range.collapse(true);
                                sel?.removeAllRanges();
                                sel?.addRange(range);
                                foundSpace = true;
                            }
                        });

                        if (!foundSpace) {
                            editor.focus();
                            if (editor.innerHTML === '' || editor.innerHTML === '<br>') {
                                const range = document.createRange();
                                const sel = window.getSelection();
                                range.selectNodeContents(editor);
                                range.collapse(false);
                                sel?.removeAllRanges();
                                sel?.addRange(range);
                            }
                        }
                    }
                }}

                data-placeholder={placeholder}
                className="support-editor-content"
            />

            <ImageZoomModal
                imageUrl={zoomImage}
                onClose={() => {
                    setZoomImage(null);
                    setZoomScale(1);
                }}
                zoomScale={zoomScale}
                onZoomChange={setZoomScale}
            />
        </div>
    );
};